import {Button, Modal} from "antd";
import {useEffect, useState} from "react";
import OTPInput from "react-otp-input";
import styled from "styled-components";
import {
  useDisableEmailTwoFactor,
  useDisableTotpTwoFactor,
  useDisableTwoFactorRequest,
} from "../../../react-query-hooks/two-factor";
import {
  StyledOtpInput,
  StyledTextButton,
} from "../../payouts/v2/tabs/payout-settings-tab/otp-view/styles";

const StyledDisableOtpInput = styled(StyledOtpInput)`
  && {
    flex: 1 1 0;
    width: 100% !important;
    min-width: 0;
    height: 6.4rem !important;
    border-radius: 8px;
    font-family: Inter;
    font-weight: 400;
    font-size: 24px;
    line-height: 32px;
    letter-spacing: 0;
    text-align: center;
  }
`;

interface TwoFactorDisableModalProps {
  visible: boolean;
  method: "email" | "totp";
  onClose: () => void;
  onSuccess: () => void;
}

export function TwoFactorDisableModal({
  visible,
  method,
  onClose,
  onSuccess,
}: TwoFactorDisableModalProps) {
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(30);

  const requestDisableMutation = useDisableTwoFactorRequest();
  const disableEmailMutation = useDisableEmailTwoFactor();
  const disableTotpMutation = useDisableTotpTwoFactor();

  useEffect(() => {
    if (visible) {
      setOtp("");
      setTimer(30);
      if (method === "email") {
        requestDisableMutation.mutate();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (!visible || method !== "email") {
      return;
    }

    const timerCount = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerCount);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerCount);
  }, [visible, method]);

  const handleResend = () => {
    requestDisableMutation.mutate(undefined, {
      onSuccess: () => {
        setTimer(30);
        setOtp("");
      },
    });
  };

  const handleSubmit = () => {
    if (method === "email") {
      disableEmailMutation.mutate(
        {twofaCode: otp},
        {onSuccess, onError: () => setOtp("")}
      );
    } else {
      disableTotpMutation.mutate(
        {twofaCode: otp},
        {onSuccess, onError: () => setOtp("")}
      );
    }
  };

  const isLoading = disableEmailMutation.isLoading || disableTotpMutation.isLoading;

  return (
    <Modal
      title="Confirm it's you"
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={520}
      bodyStyle={{padding: 24}}
    >
      <div style={{display: "flex", flexDirection: "column", gap: "20px"}}>
        <div style={{display: "flex", flexDirection: "column", gap: "8px"}}>
          <h3 style={{margin: 0}}>Disable two-factor authentication</h3>
          <p style={{margin: 0}}>
            {method === "email"
              ? "Your account will only be protected by your password. Enter the OTP sent to your email"
              : "Your account will only be protected by your password. Enter the current code in your authenticator app"}
          </p>
        </div>

        <OTPInput
          value={otp}
          onChange={setOtp}
          numInputs={6}
          shouldAutoFocus
          inputType="tel"
          containerStyle={{display: "flex", gap: "12px", width: "100%"}}
          renderInput={(props) => <StyledDisableOtpInput {...props} />}
        />

        {method === "email" && (
          <p style={{margin: 0}}>
            Didn&apos;t get code?{" "}
            {timer > 0 ? (
              <span style={{color: "rgba(0,0,0,0.45)"}}>Resend in {timer}s</span>
            ) : (
              <StyledTextButton
                type="text"
                size="small"
                loading={requestDisableMutation.isLoading}
                onClick={handleResend}
              >
                <strong style={{fontWeight: 600}}>Resend code</strong>
              </StyledTextButton>
            )}
          </p>
        )}

        <div style={{display: "flex", gap: "12px", width: "100%"}}>
          <Button size="small" style={{flex: 1}} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="small"
            style={{flex: 1}}
            danger
            type="primary"
            disabled={otp.length < 6}
            loading={isLoading}
            onClick={handleSubmit}
          >
            Disable
          </Button>
        </div>
      </div>
    </Modal>
  );
}
